import { useState, useEffect, useRef } from "react";
import { systemAPI } from "../utils/api";

const LEVEL_STYLE = {
  "ERROR":    { bg: "#FEE2E2", color: "#DC2626", dot: "#DC2626" },
  "CRITICAL": { bg: "#7F1D1D", color: "#FFF",    dot: "#DC2626" },
  "WARNING":  { bg: "#FEF3C7", color: "#D97706", dot: "#D97706" },
  "INFO":     { bg: "#DBEAFE", color: "#2563EB", dot: "#2563EB" },
};

function StatCard({ label, value, sub, color = "#4F46E5" }) {
  return (
    <div className="stat-card" style={{ borderTop: `3px solid ${color}` }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color, fontSize: 22 }}>{value ?? "—"}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

const BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production.up.railway.app");

function BackupRestore() {
  const [backupStatus, setBackupStatus] = useState(null);
  const [restoreFile,  setRestoreFile]  = useState(null);
  const [polling,      setPolling]      = useState(false);
  const [msg,          setMsg]          = useState("");
  const [msgType,      setMsgType]      = useState("green");
  // Track immediate button loading state (before API responds)
  const [backupLoading,  setBackupLoading]  = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res  = await fetch(BASE_URL + "/backup/status");
      const data = await res.json();
      setBackupStatus(data);
      return data;
    } catch { return null; }
  };

  useEffect(() => { fetchStatus(); }, []);

  const startPolling = () => {
    setPolling(true);
    pollRef.current = setInterval(async () => {
      const data = await fetchStatus();
      if (data && !data.backup?.running && !data.restore?.running) {
        clearInterval(pollRef.current);
        setPolling(false);
        setBackupLoading(false);
        setRestoreLoading(false);
      }
    }, 2000);
  };

  const handleCreateBackup = async () => {
    setMsg("");
    setBackupLoading(true);  // immediate feedback
    try {
      const res  = await fetch(BASE_URL + "/backup/create", { method: "POST" });
      const data = await res.json();
      setMsg(data.message || "Backup started");
      setMsgType("green");
      startPolling();
    } catch (e) {
      setMsg("Backup failed: " + e.message);
      setMsgType("red");
      setBackupLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    setMsg("");
    setRestoreLoading(true);  // immediate feedback — button shows loading right away
    const formData = new FormData();
    formData.append("file", restoreFile);
    try {
      const res  = await fetch(BASE_URL + "/backup/restore", { method: "POST", body: formData });
      const data = await res.json();
      setMsg(data.message || "Restore started — this may take a few minutes");
      setMsgType("green");
      startPolling();
    } catch (e) {
      setMsg("Restore failed: " + e.message);
      setMsgType("red");
      setRestoreLoading(false);
    }
  };

  const isBackupRunning  = backupStatus?.backup?.running  || backupLoading;
  const isRestoreRunning = backupStatus?.restore?.running || restoreLoading;
  const lastBackup       = backupStatus?.backup?.last_file;
  const restoreSummary   = backupStatus?.restore?.summary;
  const availableBackups = backupStatus?.available_backups || [];

  // Only show error if there's an error AND no successful summary
  // (fixes the false "failed" message when restore actually completed)
  const restoreError = backupStatus?.restore?.error && !isRestoreRunning && !restoreSummary
    ? backupStatus.restore.error
    : null;
  const restoreWarning = backupStatus?.restore?.error && !isRestoreRunning && restoreSummary
    ? backupStatus.restore.error
    : null;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <div style={{ fontWeight: 700, fontSize: 15 }}>Backup & Restore</div>
        <button className="btn btn-ghost btn-sm" onClick={fetchStatus}>↻ Refresh</button>
      </div>
      <div className="card-body">
        {msg && <div className={"alert alert-" + msgType} style={{ marginBottom: 14 }}>{msg}</div>}

        {/* Create Backup */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Create Backup</div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>Full backup includes all patient records, report values, PDFs and imaging files. Auto-runs at midnight BD time.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={handleCreateBackup} disabled={isBackupRunning || polling}>
              {isBackupRunning ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                  Creating backup...
                </span>
              ) : "⬇ Create Backup Now"}
            </button>
            {availableBackups.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => window.open(BASE_URL + "/backup/download-latest", "_blank")}>⬇ Download Latest</button>
            )}
          </div>
          {isBackupRunning && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>Backup in progress...</div>
              <div style={{ height: 4, background: "#E4E8F0", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", background: "#4F46E5", borderRadius: 99, width: "60%", animation: "pulse 1.5s ease-in-out infinite" }} />
              </div>
            </div>
          )}
          {lastBackup && !isBackupRunning && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#16A34A" }}>
              ✓ Last backup: {lastBackup}
              {backupStatus?.backup?.drive_uploaded ? " · ✓ Uploaded to Cloudflare R2" : " · ⚠ R2 upload failed"}
            </div>
          )}
        </div>

        {/* Available Backups */}
        {availableBackups.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Available Backups</div>
            {availableBackups.map((b, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#F8FAFC", borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12, fontFamily: "monospace" }}>{b.filename}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>{b.created} · {b.size_mb} MB</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => window.open(BASE_URL + "/backup/download/" + b.filename, "_blank")}>⬇</button>
              </div>
            ))}
          </div>
        )}

        {/* Restore */}
        <div style={{ borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Restore from Backup</div>
          <div className="alert alert-amber" style={{ marginBottom: 12, fontSize: 12 }}>⚠ Restore re-imports all records and files. Existing records will be skipped.</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer", opacity: isRestoreRunning ? 0.6 : 1 }}>
              {restoreFile ? restoreFile.name : "📁 Select Backup Zip"}
              <input type="file" accept=".zip" style={{ display: "none" }} onChange={e => { setRestoreFile(e.target.files[0]); setMsg(""); }} disabled={isRestoreRunning} />
            </label>
            {restoreFile && (
              <>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleRestore}
                  disabled={isRestoreRunning}
                  style={{ minWidth: 120 }}
                >
                  {isRestoreRunning ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                      Restoring...
                    </span>
                  ) : "▶ Restore Now"}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setRestoreFile(null); setMsg(""); }} disabled={isRestoreRunning} style={{ color: "#DC2626" }}>✕</button>
              </>
            )}
          </div>

          {/* Progress bar during restore */}
          {isRestoreRunning && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", marginBottom: 6 }}>
                <span>{backupStatus?.restore?.step_label || "Processing restore..."}</span>
                {backupStatus?.restore?.total > 0 && (
                  <span>{backupStatus?.restore?.progress}/{backupStatus?.restore?.total}</span>
                )}
              </div>
              <div style={{ height: 6, background: "#E4E8F0", borderRadius: 99, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 99, background: "#4F46E5",
                  width: backupStatus?.restore?.total > 0
                    ? `${Math.round((backupStatus.restore.progress / backupStatus.restore.total) * 100)}%`
                    : "30%",
                  transition: "width 0.3s ease"
                }} />
              </div>
            </div>
          )}

          {/* Error — only show if NO summary (truly failed) */}
          {restoreError && (
            <div className="alert alert-red" style={{ marginTop: 10, fontSize: 12 }}>
              ✗ Restore failed: {restoreError}
            </div>
          )}

          {/* Warning — minor issue but restore completed */}
          {restoreWarning && (
            <div className="alert alert-amber" style={{ marginTop: 10, fontSize: 12 }}>
              ⚠ Restore completed with a minor issue: {restoreWarning}
            </div>
          )}

          {/* Success summary */}
          {restoreSummary && !isRestoreRunning && (
            <div style={{ marginTop: 10, padding: "12px 14px", background: "#F0FDF4", borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: "#16A34A", marginBottom: 6 }}>✓ Restore Complete</div>
              {[
                ["Patients",      restoreSummary.patients],
                ["Reports",       restoreSummary.reports],
                ["Report Values", restoreSummary.report_values],
                ["Imaging",       restoreSummary.imaging],
                ["Report Files",  restoreSummary.report_files],
                ["Imaging Files", restoreSummary.imaging_files],
              ].map(([label, s]) => s && (
                <div key={label} style={{ display: "flex", gap: 16, marginBottom: 2 }}>
                  <span style={{ color: "#475569", minWidth: 110 }}>{label}:</span>
                  <span style={{ color: "#16A34A" }}>✓ {s.restored} restored</span>
                  {s.skipped > 0 && <span style={{ color: "#D97706" }}>{s.skipped} skipped</span>}
                  {s.failed  > 0 && <span style={{ color: "#DC2626" }}>{s.failed} failed</span>}
                </div>
              ))}
              {restoreSummary.errors?.length > 0 && (
                <div style={{ marginTop: 8, color: "#DC2626" }}>{restoreSummary.errors.length} minor error(s)</div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
      `}</style>
    </div>
  );
}

export default function SystemStatus() {
  const [status,   setStatus]   = useState(null);
  const [errors,   setErrors]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [errLoad,  setErrLoad]  = useState(false);
  const [lastCheck,setLastCheck]= useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        systemAPI.status(),
        systemAPI.errors(50),
      ]);
      setStatus(s);
      setErrors(e.errors || []);
      setLastCheck(new Date().toLocaleTimeString());
      setErrLoad(false);
    } catch (err) {
      setErrLoad(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="empty-state" style={{ paddingTop: 64 }}>
      <p>Loading system status...</p>
    </div>
  );

  if (errLoad) return (
    <div>
      <div className="alert alert-red">Cannot connect to backend. Make sure the server is running.</div>
      <button className="btn btn-secondary btn-sm" onClick={load}>Retry</button>
    </div>
  );

  const isOk     = status?.status === "ok";
  const dbType   = status?.database?.type || "Unknown";
  const storage  = status?.storage?.backend || "unknown";
  const apiKey   = status?.ai?.api_key === "configured";
  const errors24 = status?.errors_24h ?? 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>System Status</h1>
          <p style={{ color: "#64748B", fontSize: 13 }}>Last checked: {lastCheck} · v{status?.version}</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <div className={`alert ${isOk ? "alert-green" : "alert-red"}`} style={{ marginBottom: 20, fontSize: 14, fontWeight: 700 }}>
        {isOk ? "✅ All systems operational" : "❌ System issue detected — check details below"}
      </div>

      <div className="stat-grid" style={{ marginBottom: 20 }}>
        <StatCard label="Database"     value={dbType}    sub={`${status?.database?.patients ?? 0} patients, ${status?.database?.reports ?? 0} reports`} color="#0D9488" />
        <StatCard label="File Storage" value={storage.charAt(0).toUpperCase() + storage.slice(1)} sub={storage === "supabase" ? "Supabase buckets active" : storage === "r2" ? "Cloudflare R2 active" : "Local filesystem"} color="#4F46E5" />
        <StatCard label="AI Extraction" value={apiKey ? "Active" : "⚠ Missing Key"} sub={status?.ai?.model} color={apiKey ? "#16A34A" : "#DC2626"} />
        <StatCard label="Errors (24h)" value={errors24} sub={errors24 > 0 ? "Review below" : "No errors"} color={errors24 > 0 ? "#DC2626" : "#16A34A"} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div style={{ fontWeight: 700, fontSize: 14 }}>System Checklist</div></div>
        <div className="card-body">
          {[
            { label: "Backend API",       ok: isOk,           note: isOk ? "Responding normally" : "API unreachable" },
            { label: "Database",          ok: !!status?.database, note: dbType },
            { label: "Anthropic API Key", ok: apiKey,         note: apiKey ? "Configured" : "⚠ MISSING — reports will not extract" },
            { label: "File Storage",      ok: true,           note: storage === "r2" ? "Cloudflare R2 (persistent)" : storage === "supabase" ? "Supabase (persistent)" : "Local (not persistent on Railway)" },
            { label: "Errors last 24h",   ok: errors24 === 0, note: errors24 === 0 ? "Clean" : `${errors24} error(s) found` },
          ].map(({ label, ok, note }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: ok ? "#DCFCE7" : "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>
                {ok ? "✓" : "✗"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>{note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div style={{ fontWeight: 700, fontSize: 14 }}>UptimeRobot Setup</div>
          <a href="https://uptimerobot.com" target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">Open UptimeRobot →</a>
        </div>
        <div className="card-body">
          <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.8 }}>
            <p style={{ marginBottom: 8 }}>Monitor your backend for free. UptimeRobot checks every 5 minutes and emails you if the server goes down.</p>
            <div style={{ background: "#F8FAFC", borderRadius: 8, padding: "12px 16px", fontFamily: "monospace", fontSize: 12, color: "#0F172A" }}>
              Monitor URL: <strong>{window.location.origin.replace("3000", "8000")}/health</strong><br />
              Monitor type: HTTP(S)<br />
              Check interval: Every 5 minutes
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            Recent Error Log
            {errors.length > 0 && <span className="badge badge-red" style={{ marginLeft: 8 }}>{errors.length}</span>}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
        </div>
        {errors.length === 0 ? (
          <div className="empty-state" style={{ padding: "32px 0" }}>
            <p style={{ color: "#16A34A", fontWeight: 600 }}>✅ No errors recorded</p>
          </div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {errors.map((e, i) => {
              const s = LEVEL_STYLE[e.level] || LEVEL_STYLE["INFO"];
              return (
                <div key={i} style={{ padding: "12px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, marginTop: 5, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3, flexWrap: "wrap" }}>
                      <span style={{ background: s.bg, color: s.color, padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{e.level}</span>
                      <span style={{ fontSize: 11, color: "#94A3B8" }}>{e.timestamp?.slice(0, 19).replace("T", " ")}</span>
                      {e.path && <span style={{ fontSize: 11, color: "#64748B", fontFamily: "monospace" }}>{e.method} {e.path}</span>}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", marginBottom: 2 }}>{e.message}</div>
                    {e.detail && e.detail.length > 0 && (
                      <details>
                        <summary style={{ fontSize: 11, color: "#64748B", cursor: "pointer" }}>Show detail</summary>
                        <pre style={{ fontSize: 10, color: "#475569", background: "#F8FAFC", padding: 8, borderRadius: 6, marginTop: 4, overflowX: "auto", whiteSpace: "pre-wrap" }}>{e.detail}</pre>
                      </details>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BackupRestore />
    </div>
  );
}